import React, { useEffect, useMemo, useState } from "react";
import {
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import api from "../../../services/api";
import VideoLoader from "../../../components/VideoLoader";
import ReportsFilters from "../../../components/reports/ReportsFilters";
import SalesSummaryCards from "../../../components/reports/SalesSummaryCards";
import OrdersBySourceChart from "../../../components/reports/OrdersBySourceChart";
import {
  buildDefaultFilters,
  buildReportParams,
  currency,
  ReportFilters,
} from "./filterUtils";

interface SalesReportResponse {
  totalOrders: number;
  totalRevenue: number;
  onlineOrders: number;
  instoreOrders: number;
  ordersBySource: Array<{
    source: string;
    label: string;
    orders: number;
    revenue: number;
  }>;
  ordersByType: Array<{
    type: string;
    label: string;
    orders: number;
    revenue: number;
  }>;
  salesOverTime: Array<{
    date: string;
    orders: number;
    revenue: number;
  }>;
}

const initialData: SalesReportResponse = {
  totalOrders: 0,
  totalRevenue: 0,
  onlineOrders: 0,
  instoreOrders: 0,
  ordersBySource: [],
  ordersByType: [],
  salesOverTime: [],
};

const PIE_COLORS = ["#2563eb", "#60a5fa", "#93c5fd", "#dbeafe"];
const formatRevenue = (value: number) => `$${Number(value).toFixed(2)}`;

const SalesReport: React.FC = () => {
  const [filters, setFilters] = useState<ReportFilters>(buildDefaultFilters());
  const [data, setData] = useState<SalesReportResponse>(initialData);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSalesReport = async () => {
      try {
        setLoading(true);
        const res = await api.get<SalesReportResponse>("/reports/sales", {
          params: buildReportParams(filters),
        });
        setData(res.data);
      } catch (error) {
        console.error("Failed to fetch sales report", error);
        setData(initialData);
      } finally {
        setLoading(false);
      }
    };

    fetchSalesReport();
  }, [filters]);

  const pieData = useMemo(
    () =>
      data.ordersByType.filter(
        (entry) => entry.orders > 0 || entry.revenue > 0,
      ),
    [data.ordersByType],
  );
  const hasOrdersBySourceData = useMemo(
    () =>
      data.ordersBySource.some((entry) => entry.orders > 0 || entry.revenue > 0),
    [data.ordersBySource],
  );
  const hasSalesOverTimeData = useMemo(
    () =>
      data.salesOverTime.some((entry) => entry.orders > 0 || entry.revenue > 0),
    [data.salesOverTime],
  );

  return (
    <div className="mx-auto h-screen max-w-7xl overflow-y-auto no-scrollbar p-4 pt-0 lg:p-8">
      <div className="sticky top-0 z-10 bg-white/90 py-6 backdrop-blur-sm">
        <h1 className="text-2xl font-bold text-gray-800">Sales Report</h1>
      </div>

      <ReportsFilters value={filters} onChange={setFilters} />
      {loading ? (
        <div className="flex min-h-[400px] items-center justify-center">
          <VideoLoader />
        </div>
      ) : (
        <>
          <SalesSummaryCards
            totalOrders={data.totalOrders}
            totalRevenue={data.totalRevenue}
            onlineOrders={data.onlineOrders}
            instoreOrders={data.instoreOrders}
          />

          <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <h2 className="mb-3 text-lg font-semibold text-gray-800">
                Orders by Source
              </h2>
              <div className="mb-4 overflow-x-auto">
                <table className="min-w-full border-collapse">
                  <thead className="bg-gray-50 text-xs text-gray-700 sm:text-sm">
                    <tr className="border-b border-gray-200">
                      <th className="p-3 text-left font-semibold">Source</th>
                      <th className="p-3 text-center font-semibold">Orders</th>
                      <th className="p-3 text-center font-semibold">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.ordersBySource.map((row) => (
                      <tr
                        key={row.source}
                        className="border-b border-gray-200 hover:bg-gray-50"
                      >
                        <td className="p-3 text-sm text-gray-700">{row.label}</td>
                        <td className="p-3 text-center text-sm text-gray-700">
                          {row.orders}
                        </td>
                        <td className="p-3 text-center text-sm text-gray-700">
                          {currency(row.revenue)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {hasOrdersBySourceData ? (
                <OrdersBySourceChart data={data.ordersBySource} />
              ) : (
                <div className="flex h-[280px] items-center justify-center text-sm text-gray-500">
                  No data available for selected filters.
                </div>
              )}
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <h2 className="mb-3 text-lg font-semibold text-gray-800">
                Orders by Type
              </h2>
              <div className="mb-4 overflow-x-auto">
                <table className="min-w-full border-collapse">
                  <thead className="bg-gray-50 text-xs text-gray-700 sm:text-sm">
                    <tr className="border-b border-gray-200">
                      <th className="p-3 text-left font-semibold">Type</th>
                      <th className="p-3 text-center font-semibold">Orders</th>
                      <th className="p-3 text-center font-semibold">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.ordersByType.map((row) => (
                      <tr
                        key={row.type}
                        className="border-b border-gray-200 hover:bg-gray-50"
                      >
                        <td className="p-3 text-sm text-gray-700">{row.label}</td>
                        <td className="p-3 text-center text-sm text-gray-700">
                          {row.orders}
                        </td>
                        <td className="p-3 text-center text-sm text-gray-700">
                          {currency(row.revenue)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pieData.length > 0 ? (
                <div className="h-[280px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="orders"
                        nameKey="label"
                        outerRadius={95}
                        label
                      >
                        {pieData.map((entry, index) => (
                          <Cell
                            key={`${entry.type}-${index}`}
                            fill={PIE_COLORS[index % PIE_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex h-[280px] items-center justify-center text-sm text-gray-500">
                  No data available for selected filters.
                </div>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <h2 className="mb-3 text-lg font-semibold text-gray-800">
              Sales Over Time
            </h2>
            {hasSalesOverTimeData ? (
              <div className="h-[320px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={data.salesOverTime}
                    margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis
                      yAxisId="left"
                      tick={{ fontSize: 12 }}
                      allowDecimals={false}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tick={{ fontSize: 12 }}
                      tickFormatter={formatRevenue}
                    />
                    <Tooltip
                      formatter={(value, name) =>
                        name === "Daily Revenue"
                          ? [formatRevenue(Number(value)), name]
                          : [Number(value), name]
                      }
                    />
                    <Legend />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="orders"
                      stroke="#2563eb"
                      strokeWidth={2}
                      dot={false}
                      name="Daily Orders"
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="revenue"
                      stroke="#60a5fa"
                      strokeWidth={2}
                      dot={false}
                      name="Daily Revenue"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex h-[320px] items-center justify-center text-sm text-gray-500">
                No data available for selected filters.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SalesReport;
